'use client'

import { SupportedLanguages } from '@/types/dictionary';
import { useRouter, usePathname } from 'next/navigation'
import { useLang } from '@/app/context/LangContext';
import { BookingSubject } from '@/types/calendar';

class RouterManager {
  private router: ReturnType<typeof useRouter>;
  private pathname: string;
  public lang: string;

  public readonly MAIN_PAGE_APP_PATH = "/";
  public readonly AI_ADVISOR_SECTION_PATH = "#ai-advisor";
  public readonly CASE_STUDIES_SECTION_PATH = "#case-studies";
  public readonly SERVICES_SECTION_PATH = "#services";
  public readonly DOC_BRAIN_PAGE_PATH = "docbrain-ai-agent";
  public readonly AI_SYSTEM_DEVELOPMENT_PAGE_PATH = "services/ai-system-development";
  public readonly SOFTWARE_COMPANY_VS_STUDIO_PAGE_PATH = "services/software-company-vs-digital-studio";
  public readonly CUSTOM_SOFTWARE_DEVELOPMENT_PAGE_PATH = "services/custom-software-development";
  public readonly AI_BLUEPRINT_PAGE_PATH = "ai-blueprint";
  public readonly AI_AGENTS_LP_PAGE_PATH = "lp/ai-agents";
  public readonly PRIVACY_POLICY_PAGE_PATH = "privacy-policy";
  public readonly BOOKING_PAGE_PATH = "booking";
  public readonly BOOKING_SUCCESS_PAGE_PATH = `${this.BOOKING_PAGE_PATH}/success`;
  public readonly BOOKING_FAILED_PAGE_PATH = `${this.BOOKING_PAGE_PATH}/failed`;
  public readonly FROM_NEWS_PAGE_PATH = "from-news";
  public readonly CONTACT_US_PAGE_PATH = "contact-us";
  public readonly CONTACT_US_SUCCESS_PAGE_PATH = `${this.CONTACT_US_PAGE_PATH}/success`;
  public readonly CONTACT_US_FAILED_PAGE_PATH = `${this.CONTACT_US_PAGE_PATH}/failed`;
  public readonly BLOG_PAGE_PATH = "magazine";
  public readonly INDUSTRIES_SECTION_PATH = "#industries";

  constructor(router: ReturnType<typeof useRouter>, pathname: string, lang: string) {
    this.router = router;
    this.pathname = pathname;
    this.lang = lang;
  }

  goTo = (path: string) => {
    const cleanedPath = path.startsWith("/") ? path.slice(1) : path;
    this.router.push(`/${this.lang}/${cleanedPath}`);
  };

  goToBookingPage = (subject: BookingSubject) => {
    this.goTo(`${this.BOOKING_PAGE_PATH}?subject=${subject}`);
  };

  goBack = () => {
    this.router.back();
  };

  changeLang = (lang: SupportedLanguages) => {
    const currentPath = this.currentPath()
    const pathWithoutLang = currentPath.replace(/^\/[^\/]+/, '') || '/'
    const newUrl = `/${lang}${pathWithoutLang}`
    this.router.push(newUrl);
  };

  currentPath = () => {
    if (typeof window === 'undefined') return this.pathname;
    return window.location.pathname;
  };
}

export function useRouterManager() {
  const router = useRouter();
  const pathname = usePathname() || "";
  const lang = useLang();
  return new RouterManager(router, pathname, lang);
}
